import { Link } from 'react-router-dom';
import type { Movie } from '@/mocks/movies';
import Rating from '@/components/base/Rating';
import FavoriteButton from '@/components/feature/FavoriteButton';
import { formatMeta } from '@/lib/catalog';

interface MovieHeroProps {
  movie: Movie;
}

export default function MovieHero({ movie }: MovieHeroProps) {
  const typeLabel = movie.type === 'movie' ? '电影' : '电视剧';
  const listTo = movie.type === 'movie' ? '/category/movie' : '/category/tv';

  return (
    <section className="relative w-full overflow-hidden bg-primary-950">
      <img
        src={movie.backdrop}
        alt={`${movie.title} 剧照`}
        title={`${movie.title} 详情`}
        className="absolute inset-0 h-full w-full object-cover object-top opacity-40"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-primary-950 via-primary-950/90 to-primary-950/70" />

      <div className="relative mx-auto max-w-7xl px-4 py-8 md:px-8 md:py-12">
        <nav aria-label="面包屑导航" className="mb-6 flex flex-wrap items-center gap-2 text-sm text-background-300">
          <Link to="/" className="transition-colors hover:text-accent-400 cursor-pointer">首页</Link>
          <i className="ri-arrow-right-s-line" aria-hidden="true" />
          <Link to={listTo} className="transition-colors hover:text-accent-400 cursor-pointer">
            {typeLabel}
          </Link>
          <i className="ri-arrow-right-s-line" aria-hidden="true" />
          <span className="text-background-100">{movie.title}</span>
        </nav>

        <div className="flex flex-col gap-6 md:flex-row md:items-end">
          <div className="w-40 flex-shrink-0 overflow-hidden rounded-lg border border-primary-800 md:w-52">
            <img
              src={movie.poster}
              alt={`${movie.title} 海报`}
              title={`${movie.title} 海报`}
              className="aspect-[2/3] h-full w-full object-cover object-top"
            />
          </div>

          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <span className="rounded-md bg-accent-500 px-2.5 py-0.5 font-label text-xs font-semibold text-primary-950">
                {typeLabel}
              </span>
              <span className="rounded-md bg-primary-800 px-2.5 py-0.5 font-label text-xs text-background-200">
                {movie.status}
              </span>
            </div>

            <h1 className="mt-3 font-heading text-3xl font-black text-background-50 md:text-4xl">
              {movie.title}
            </h1>
            <p className="mt-1 font-label text-sm text-background-400">{movie.originalTitle}</p>

            <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 font-label text-sm text-background-200">
              <span className="flex items-center gap-1 text-lg">
                <i className="ri-star-fill text-accent-400" aria-hidden="true" />
                <strong className="font-heading text-xl font-black text-accent-400">
                  {movie.rating.toFixed(1)}
                </strong>
              </span>
              <span>{movie.year}</span>
              <span>{movie.region}</span>
              <span>{movie.genres.join(' / ')}</span>
              <span>{formatMeta(movie)}</span>
            </div>

            <div className="mt-5 flex flex-wrap items-center gap-3">
              <FavoriteButton movieId={movie.id} />
              <Link
                to={listTo}
                className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-200/50 px-4 py-2.5 font-label text-sm text-background-100 transition-colors hover:bg-background-50/10 cursor-pointer"
              >
                <i className="ri-arrow-left-line" aria-hidden="true" />
                返回{typeLabel}列表
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}